import { marketDate } from "./upcoming-earnings-data-source.js";

const DEFAULT_INTERVAL = 15 * 60 * 1000;

// This controls reads of the stored feed. It does not schedule provider downloads.
export class VisibleFeedRefreshController {
  constructor({
    refresh, intervalMs = DEFAULT_INTERVAL, now = () => new Date(),
    setTimeoutImpl = globalThis.setTimeout.bind(globalThis),
    clearTimeoutImpl = globalThis.clearTimeout.bind(globalThis),
    documentTarget = globalThis.document
  }) {
    if (typeof refresh !== "function") throw new TypeError("A feed refresh callback is required.");
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) throw new TypeError("A positive refresh interval is required.");
    this.refresh = refresh;
    this.intervalMs = intervalMs;
    this.now = now;
    this.setTimeoutImpl = setTimeoutImpl;
    this.clearTimeoutImpl = clearTimeoutImpl;
    this.documentTarget = documentTarget;
    this.active = false;
    this.disposed = false;
    this.inFlight = false;
    this.timer = null;
    this.lastRefreshedAt = null;
    this.lastMarketDate = null;
    this.onVisibilityChange = () => {
      this.clearTimer();
      if (!this.active || !this.visible()) return;
      const instant = this.instant();
      if (this.due(instant)) this.run();
      else this.schedule();
    };
    this.documentTarget?.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  start() {
    if (this.disposed || this.active) return;
    this.active = true;
    this.markRefreshed();
  }

  stop() { this.active = false; this.clearTimer(); }

  markRefreshed(now = this.instant()) {
    if (this.disposed) return;
    const instant = new Date(now);
    this.lastRefreshedAt = instant.getTime();
    this.lastMarketDate = marketDate(instant);
    this.schedule();
  }

  visible() {
    return this.documentTarget?.hidden !== true && this.documentTarget?.visibilityState !== "hidden";
  }

  instant() { return new Date(this.now()); }

  due(now) {
    return this.lastRefreshedAt === null || marketDate(now) !== this.lastMarketDate
      || now.getTime() - this.lastRefreshedAt >= this.intervalMs;
  }

  schedule() {
    this.clearTimer();
    if (!this.active || this.disposed || !this.visible() || this.inFlight) return;
    const instant = this.instant();
    const deadline = this.due(instant) ? instant.getTime() : Math.min(
      (this.lastRefreshedAt ?? instant.getTime()) + this.intervalMs,
      nextMarketDateBoundary(instant).getTime());
    this.timer = this.setTimeoutImpl(() => {
      this.timer = null;
      if (!this.active || this.disposed || !this.visible()) return;
      if (this.due(this.instant())) this.run();
      else this.schedule();
    }, Math.max(1, deadline - instant.getTime()));
  }

  async run() {
    if (!this.active || this.disposed || !this.visible() || this.inFlight) return;
    this.inFlight = true;
    this.clearTimer();
    const instant = this.instant();
    const reason = marketDate(instant) !== this.lastMarketDate ? "market-date" : "interval";
    try { await this.refresh({ reason, now: instant }); }
    catch { /* The owning window presents the feed error; keep later checks possible. */ }
    finally {
      this.inFlight = false;
      if (!this.disposed) this.markRefreshed(instant);
    }
  }

  clearTimer() {
    if (this.timer !== null) this.clearTimeoutImpl(this.timer);
    this.timer = null;
  }

  dispose() {
    this.stop(); this.disposed = true;
    this.documentTarget?.removeEventListener("visibilitychange", this.onVisibilityChange);
  }
}

// Locate the next New York date boundary without assuming a UTC offset or 24-hour day.
export function nextMarketDateBoundary(now = new Date()) {
  let low = now.getTime();
  let high = low + 26 * 60 * 60 * 1000;
  const current = marketDate(now);
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (marketDate(new Date(middle)) === current) low = middle;
    else high = middle;
  }
  return new Date(high);
}
